// FILE: src/components/studyPlanHelpers.js
//
// Shared constants + pure helpers used by StudyPlanView and its sub-components.
// Kept in its own module so the main StudyPlanView file stays digestible.

export const TYPE_LABEL = {
  practice:  'Practice session',
  targeted:  'Targeted quiz',
  full:      'Full length simulation',
  review:    'Review flagged questions',
  rest:      'Rest day',
  reading:   'Official reading',
  logistics: 'Exam logistics',
  exam_day:  'Exam day',
};

// Short chip label per task type (Command Center weekly plan).
export const TYPE_CHIP = {
  practice:  'Practice',
  targeted:  'Practice',
  full:      'Simulation',
  review:    'Review',
  rest:      'Rest',
  reading:   'Reading',
  logistics: 'Logistics',
  exam_day:  'Exam Day',
};

/**
 * Normalize a study_plan_activities row (legacy or 069-era) into the
 * plan-item view model the Command Center components render. Legacy rows
 * (title = null) fall back to TYPE_LABEL — old plans render unchanged.
 */
export function toPlanItem(a) {
  const type = a.activity_type;
  const startable = ['practice', 'targeted', 'full', 'review'].includes(type);
  return {
    id: a.id,
    raw: a,
    taskType: type,
    date: a.scheduled_for,
    title: a.title || TYPE_LABEL[type] || type,
    description: a.description || '',
    estimatedMinutes: a.target_minutes || null,
    questionCount: a.recommended_count || null,
    status: a.status,
    focusPhase: a.focus_phase || null,
    linkedDomain: a.linked_domain || null,
    linkedTask: a.linked_task || null,
    sourceReference: a.source_reference || null,
    canStart: startable,
    checkable: !startable && type !== 'rest',
  };
}

export const TYPE_EMOJI = {
  practice: 'P',
  targeted: 'T',
  full:     'S',
  review:   'R',
  rest:     '~',
};

export const JOURNEY_PHASES = [
  { id: 'foundation', label: 'Foundation',    icon: '/themes/pm-pixel/Journey/roadmap_baseline.png' },
  { id: 'practice',   label: 'Practice',      icon: '/themes/pm-pixel/Journey/roadmap_practice.png' },
  { id: 'targeted',   label: 'Targeted Work', icon: '/themes/pm-pixel/Journey/roadmap_targeted.png' },
  { id: 'simulation', label: 'Simulation',    icon: '/themes/pm-pixel/Journey/roadmap_sim.png' },
  { id: 'final',      label: 'Final Review',  icon: '/themes/pm-pixel/Journey/roadmap_ready.png' },
  { id: 'exam',       label: 'Exam Day',      icon: '/themes/pm-pixel/Journey/roadmap_certified.png' },
];

export const PMI_REFERENCES = [
  { title: 'PMBOK Guide - 8th Ed.', priority: 'new', priorityLabel: 'New',
    url: 'https://www.pmi.org/pmbok-guide-standards/foundational/pmbok',
    desc: 'Latest PMI standard and foundational reference for PMP preparation.' },
  { title: 'PMP Exam Content Outline (ECO)', priority: 'critical', priorityLabel: 'Exam Critical',
    url: 'https://www.pmi.org/-/media/pmi/documents/public/pdf/certifications/pmp-examination-content-outline.pdf',
    desc: 'Defines the PMP exam domains, tasks, and enablers. The primary exam blueprint.' },
  { title: 'Agile Practice Guide', priority: 'high', priorityLabel: 'High Value',
    url: 'https://www.pmi.org/pmbok-guide-standards/practice-guides/agile',
    desc: 'Agile, hybrid, and adaptive approaches represented throughout the PMP exam.' },
  { title: 'Process Groups Practice Guide', priority: 'reference', priorityLabel: 'Reference',
    url: 'https://www.pmi.org/pmbok-guide-standards/practice-guides/process-groups-practice-guide',
    desc: 'Modern interpretation of process groups and predictive project management practices.' },
  { title: 'PMBOK Guide - 7th Ed.', priority: 'reference', priorityLabel: 'Reference',
    url: 'https://www.pmi.org/pmbok-guide-standards/foundational/pmbok',
    desc: 'Previous edition still referenced by many PMP study resources and instructors.' },
];

// CPMAI-specific references shown on the Study Guide when the active cert is
// CPMAI. Same shape as PMI_REFERENCES so ResourceCard renders them identically.
export const CPMAI_REFERENCES = [
  { title: 'The Standard for AI in Portfolio, Program and Project Management', priority: 'new', priorityLabel: 'New',
    url: 'https://www.pmi.org/standards/artificial-intelligence',
    desc: 'PMI’s standard for applying artificial intelligence across portfolio, program, and project management.' },
  { title: 'CPMAI Exam Content Outline', priority: 'critical', priorityLabel: 'Exam Critical',
    url: 'https://www.pmi.org/-/media/pmi/documents/public/pdf/certifications/pmicpmai-exam-content-outline2025-updated.pdf?rev=a88ceeb705ca45e4abb89b26c3a3cb1d',
    desc: 'Defines the CPMAI exam domains, tasks, and enablers. The primary exam blueprint (2025 updated).' },
  { title: 'Leading & Managing AI Projects Digital Guide', priority: 'high', priorityLabel: 'High Value',
    url: 'https://www.pmi.org/standards/leading-and-managing-ai-projects-digital-guide',
    desc: 'PMI practice guide covering the practical leadership and delivery of AI projects.' },
  { title: 'PMBOK Guide - 8th Ed.', priority: 'reference', priorityLabel: 'Reference',
    url: 'https://www.pmi.org/pmbok-guide-standards/foundational/pmbok',
    desc: 'Latest PMI standard and foundational project management reference.' },
];

// Per-cert reference lists. Certs without an entry fall back to the
// PMP-centric PMI_REFERENCES list (existing behavior).
// PMI-ACP references: foundational agile documents + PMI's official
// examination reference list (11 books, blocked as exploratory reading in
// the study plan's reading catalog).
export const ACP_REFERENCES = [
  { title: 'PMI-ACP Exam Content Outline', priority: 'critical', priorityLabel: 'Exam Critical',
    url: 'https://www.pmi.org/-/media/pmi/documents/public/pdf/certifications/agile-certified-exam-outline.pdf',
    desc: 'Defines the PMI-ACP exam domains and tasks. The primary exam blueprint.' },
  { title: 'Agile Practice Guide', priority: 'high', priorityLabel: 'High Value',
    url: 'https://www.pmi.org/pmbok-guide-standards/practice-guides/agile',
    desc: 'PMI/Agile Alliance guide to agile, hybrid, and adaptive approaches.' },
  { title: 'The Agile Manifesto', priority: 'critical', priorityLabel: 'Exam Critical',
    url: 'https://agilemanifesto.org',
    desc: 'The four values and twelve principles underpinning the agile mindset.' },
  { title: 'The Scrum Guide', priority: 'high', priorityLabel: 'High Value',
    url: 'https://scrumguides.org/scrum-guide.html',
    desc: 'The definitive definition of Scrum by Schwaber and Sutherland.' },
  { title: 'PMI-ACP Reference Materials (11-book list)', priority: 'reference', priorityLabel: 'Reference',
    url: 'https://www.pmi.org/-/media/pmi/documents/public/pdf/certifications/agile-gain-insights.pdf',
    desc: 'PMI\u2019s official examination reference list — each book appears as an exploratory reading block in your plan.' },
];

export const CERT_REFERENCES = {
  pmp: PMI_REFERENCES,
  cpmai: CPMAI_REFERENCES,
  'pmi-acp': ACP_REFERENCES,
};

export const ADDITIONAL_RESOURCES = [
  { title: 'PMI Infinity AI Assistant', priority: 'recommended', priorityLabel: 'Recommended',
    url: 'https://infinity.pmi.org',
    desc: 'PMI\'s AI-powered assistant for standards, certifications, and PM guidance.' },
  { title: 'PMI Standards+', priority: 'recommended', priorityLabel: 'Recommended',
    url: 'https://standardsplus.pmi.org',
    desc: 'Searchable PMI standards repository with practical examples and implementation guidance.' },
  { title: 'PMP Certification Handbook', priority: 'recommended', priorityLabel: 'Recommended',
    url: 'https://www.pmi.org/certifications/project-management-pmp/pmp-handbook',
    desc: 'Official certification policies, eligibility requirements, and exam procedures.' },
];

// CPMAI-specific Additional Resources: swaps the PMP Certification Handbook
// for the NotebookLM study companion; shared PMI tools kept.
export const CPMAI_ADDITIONAL_RESOURCES = [
  { title: 'CPMAI Certification Guide (NotebookLM)', priority: 'high', priorityLabel: 'High Value',
    url: 'https://notebooklm.google.com/notebook/74c82dfb-03e4-49bf-b763-a93959cef0a6?pli=1',
    desc: 'PMI Certified Professional in Managing AI Certification Guide - interactive NotebookLM study companion.' },
  { title: 'PMI Infinity AI Assistant', priority: 'recommended', priorityLabel: 'Recommended',
    url: 'https://infinity.pmi.org',
    desc: 'PMI\'s AI-powered assistant for standards, certifications, and PM guidance.' },
  { title: 'PMI Standards+', priority: 'recommended', priorityLabel: 'Recommended',
    url: 'https://standardsplus.pmi.org',
    desc: 'Searchable PMI standards repository with practical examples and implementation guidance.' },
];

// Per-cert Additional Resources. Certs without an entry fall back to the
// PMP-centric ADDITIONAL_RESOURCES list (existing behavior).
export const CERT_ADDITIONAL_RESOURCES = {
  pmp: ADDITIONAL_RESOURCES,
  cpmai: CPMAI_ADDITIONAL_RESOURCES,
};

// STUB: Learning Resources point to a videos hub that doesn't exist yet.
export const LEARNING_RESOURCES = [
  { title: 'PMP Mindset',            duration: '20 min', url: '#' },
  { title: 'Agile Concepts',         duration: '18 min', url: '#' },
  { title: 'Risk Management',        duration: '22 min', url: '#' },
  { title: 'Stakeholder Engagement', duration: '16 min', url: '#' },
];

export function todayIso() { return new Date().toISOString().slice(0, 10); }

export function daysUntilDate(iso) {
  if (!iso) return null;
  const target = new Date(iso + 'T00:00:00');
  const now = new Date();
  return Math.round((target - now) / (1000 * 60 * 60 * 24));
}

export function formatDateLong(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch (_) { return iso; }
}

export function formatDateShort(iso) {
  if (!iso) return '';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (_) { return iso; }
}

export function dayShortName(iso) {
  if (!iso) return '';
  return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase();
}

export function dayOfMonth(iso) {
  if (!iso) return '';
  return new Date(iso + 'T00:00:00').getDate();
}

export function activitiesThisWeek(activities) {
  const now = new Date();
  const sun = new Date(now); sun.setDate(now.getDate() - now.getDay()); sun.setHours(0,0,0,0);
  const sat = new Date(sun); sat.setDate(sun.getDate() + 6); sat.setHours(23,59,59,999);
  return (activities || []).filter((a) => {
    if (!a.scheduled_for) return false;
    const d = new Date(a.scheduled_for + 'T00:00:00');
    return d >= sun && d <= sat;
  }).sort((a, b) => (a.scheduled_for || '').localeCompare(b.scheduled_for || ''));
}

export function formatWeekRange(now) {
  const sun = new Date(now); sun.setDate(now.getDate() - now.getDay());
  const sat = new Date(sun); sat.setDate(sun.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `Week of ${fmt(sun)} - ${fmt(sat)}`;
}

export function nextPendingActivity(activities) {
  const today = todayIso();
  const sorted = [...(activities || [])].sort((a, b) => (a.scheduled_for || '').localeCompare(b.scheduled_for || ''));
  return sorted.find((a) => a.status !== 'completed' && (a.scheduled_for || '') >= today)
    || sorted.find((a) => a.status !== 'completed')
    || null;
}

export function activityToHero(activity) {
  const titleByType = {
    review:   'Review Flagged Questions',
    targeted: 'Targeted Quiz',
    practice: 'Practice Session',
    full:     'Full Length Simulation',
    rest:     'Rest Day',
  };
  const focusLabel = activity.focus_phase || 'Practice';
  return {
    title: titleByType[activity.activity_type] || (TYPE_LABEL[activity.activity_type] || 'Next session'),
    duration: activity.activity_type === 'full' ? '4+ hrs' : '30 min',
    questionCount: activity.activity_type === 'full' ? 175 : 15,
    description: activity.description
      || 'Reviewing this set today will strengthen your understanding before the next simulation.',
    focusLabel,
    focusDesc: `Improve accuracy on ${focusLabel.toLowerCase()} questions and concepts.`,
    canStart: activity.activity_type !== 'rest',
  };
}

// STUB: phase derivation based on completion ratio.
export function derivePhaseId(activities) {
  const total = activities.length;
  if (total === 0) return 'foundation';
  const done = activities.filter((a) => a.status === 'completed').length;
  const ratio = done / total;
  if (ratio >= 0.95) return 'exam';
  if (ratio >= 0.80) return 'final';
  if (ratio >= 0.55) return 'simulation';
  if (ratio >= 0.30) return 'targeted';
  if (ratio >= 0.10) return 'practice';
  return 'foundation';
}
